/**
 * Categories — image-led carousel for cargo over 35 kg.
 *
 * The generated category images are the product here. The section uses a
 * horizontal snap carousel so the visitor scrolls through cargo types instead
 * of reading a dense selector grid.
 */

import { motion } from "framer-motion";
import { Box, Car, Package, Sailboat, Sofa, Wrench, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Container } from "@/components/ui/Container";

type CategoryKey = "furniture" | "vehicles" | "boats" | "machinery" | "pallets" | "other";

type CategoryItem = {
  key: CategoryKey;
  Icon: LucideIcon;
  image: string;
  mobileImage: string;
  objectPosition: string;
};

type CategoryCopy = {
  title: string;
  body: string;
  alt: string;
};

const items = [
  {
    key: "furniture",
    Icon: Sofa,
    image: "/landing-assets/categories/furniture-moving.png",
    mobileImage: "/landing-assets/categories/mobile/furniture-moving.webp",
    objectPosition: "50% 52%",
  },
  {
    key: "vehicles",
    Icon: Car,
    image: "/landing-assets/categories/vehicles-motorcycles.png",
    mobileImage: "/landing-assets/categories/mobile/vehicles-motorcycles.webp",
    objectPosition: "50% 55%",
  },
  {
    key: "boats",
    Icon: Sailboat,
    image: "/landing-assets/categories/boats.png",
    mobileImage: "/landing-assets/categories/mobile/boats.webp",
    objectPosition: "50% 52%",
  },
  {
    key: "machinery",
    Icon: Wrench,
    image: "/landing-assets/categories/machinery-equipment.png",
    mobileImage: "/landing-assets/categories/mobile/machinery-equipment.webp",
    objectPosition: "52% 50%",
  },
  {
    key: "pallets",
    Icon: Package,
    image: "/landing-assets/categories/pallets-parcels.png",
    mobileImage: "/landing-assets/categories/mobile/pallets-parcels.webp",
    objectPosition: "50% 52%",
  },
  {
    key: "other",
    Icon: Box,
    image: "/landing-assets/categories/other-cargo.png",
    mobileImage: "/landing-assets/categories/mobile/other-cargo.webp",
    objectPosition: "50% 52%",
  },
] satisfies [CategoryItem, ...CategoryItem[]];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 100, damping: 20 } },
};

export function Categories() {
  const { t } = useTranslation();

  return (
    <section
      className="relative isolate flex min-h-[100dvh] items-center overflow-hidden bg-slate-950 pt-[clamp(4rem,8dvh,7rem)] pb-[clamp(3rem,7dvh,8rem)] text-slate-50"
      id="kategorier"
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,#020617,#07111f_38%,#020617)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(96,165,250,0.055)_1px,transparent_1px),linear-gradient(to_bottom,rgba(96,165,250,0.045)_1px,transparent_1px)] bg-[size:64px_64px] opacity-60" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#e5ded2] to-transparent" />

      <Container className="relative max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="grid grid-cols-1 gap-8 lg:grid-cols-[0.48fr_0.52fr] lg:items-end"
        >
          <div>
            <h2 className="max-w-[12ch] text-4xl font-bold leading-[0.98] tracking-tight text-slate-50 md:text-6xl lg:translate-y-4 xl:translate-y-6">
              {t("categories.title")}
            </h2>
          </div>
          <div className="max-w-xl lg:justify-self-end">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-300">
              {t("categories.label")}
            </span>
            <p className="mt-4 text-base leading-relaxed text-slate-300 md:text-lg">
              {t("categories.subtitle")}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mt-8 lg:mt-10"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
            {items.map((item, index) => {
              const copy = t(`categories.items.${item.key}`, { returnObjects: true }) as CategoryCopy;
              const Icon = item.Icon;

              return (
                <motion.article
                  key={item.key}
                  className="group relative aspect-[3/2] max-h-[clamp(11rem,30dvh,22rem)] overflow-hidden rounded-2xl border border-slate-800/60 bg-slate-900"
                  whileHover={{ y: -3 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                >
                  <picture>
                    <source media="(max-width: 768px)" srcSet={item.mobileImage} type="image/webp" />
                    <img
                      src={item.image}
                      alt={copy.alt}
                      width={1802}
                      height={1024}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                      style={{ objectPosition: item.objectPosition }}
                    />
                  </picture>
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0)_55%,rgba(2,6,23,0.7)_100%)]" />

                  <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-slate-200/10 bg-slate-950/30 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-300/90 backdrop-blur-md">
                    <Icon className="h-3 w-3" strokeWidth={1.6} aria-hidden />
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div className="absolute inset-x-3 bottom-3 flex items-end justify-between gap-3">
                    <h3 className="text-sm font-semibold tracking-tight text-slate-50 sm:text-base">
                      {copy.title}
                    </h3>
                    <p className="hidden max-w-[14ch] text-right text-[11px] leading-snug text-slate-300/80 sm:block">
                      {copy.body}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.div>
      </Container>
    </section>
  );
}
